import { KCAL_PER_GRAM_CARBOHYDRATE, KCAL_PER_GRAM_FAT, KCAL_PER_GRAM_PROTEIN } from "../nutrition/energy";
import { requireFiniteNonNegative, requirePercentage } from "../shared/validation";

export interface MacroDistribution {
  readonly proteinPercent: number;
  readonly carbohydratePercent: number;
  readonly fatPercent: number;
}

export function validateMacroDistribution(split: MacroDistribution): MacroDistribution {
  requirePercentage(split.proteinPercent, "Protein percentage", true);
  requirePercentage(split.carbohydratePercent, "Carbohydrate percentage", true);
  requirePercentage(split.fatPercent, "Fat percentage", true);
  const sum = split.proteinPercent + split.carbohydratePercent + split.fatPercent;
  if (Math.abs(sum - 100) > 0.001) throw new Error("Macro percentages must total 100");
  if (split.carbohydratePercent + split.fatPercent <= 0) {
    throw new Error("Carbohydrate and fat percentages cannot both be zero");
  }
  return split;
}

export interface DailyMacroTargets {
  readonly calories: number;
  readonly proteinGrams: number;
  readonly carbohydrateGrams: number;
  readonly fatGrams: number;
  readonly proteinCalories: number;
  readonly carbohydrateCalories: number;
  readonly fatCalories: number;
}

/**
 * Protein is a fixed daily floor from body weight, never banked or reduced by daily
 * calorie redistribution. Remaining energy follows the normalized carb/fat pie ratio.
 */
export function calculateDailyMacroTargets(
  dailyCalories: number,
  fixedProteinGrams: number,
  preferredSplit: MacroDistribution,
  minimumFatGrams = 0,
): DailyMacroTargets {
  requireFiniteNonNegative(dailyCalories, "Daily calories");
  requireFiniteNonNegative(fixedProteinGrams, "Protein target");
  requireFiniteNonNegative(minimumFatGrams, "Minimum fat");
  validateMacroDistribution(preferredSplit);
  const proteinCalories = fixedProteinGrams * KCAL_PER_GRAM_PROTEIN;
  if (dailyCalories < proteinCalories) throw new Error("Daily calories cannot supply the fixed protein target");
  const remaining = dailyCalories - proteinCalories;
  const carbFatTotal = preferredSplit.carbohydratePercent + preferredSplit.fatPercent;
  const carbohydrateCalories = remaining * (preferredSplit.carbohydratePercent / carbFatTotal);
  const fatCalories = remaining - carbohydrateCalories;
  const fatGrams = fatCalories / KCAL_PER_GRAM_FAT;
  if (fatGrams + 0.000_001 < minimumFatGrams) {
    throw new Error(`Daily allocation and macro split must supply at least ${minimumFatGrams} g fat`);
  }
  return {
    calories: dailyCalories,
    proteinGrams: fixedProteinGrams,
    carbohydrateGrams: carbohydrateCalories / KCAL_PER_GRAM_CARBOHYDRATE,
    fatGrams,
    proteinCalories,
    carbohydrateCalories,
    fatCalories,
  };
}
